import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { OutputEc2, OutputRDS } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Container, Header, SpaceBetween, Spinner, Table, Tabs, TabsProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { constants } from '../../../common/Localization';
import { MatchParam } from '../../../common/types';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import { TcoContext } from '../../../contexts/TcoContext';
import PreviewBanner from '../../common/PreviewBanner';
import { commonTotalCostSummary, summaryColumnDefs } from '../common/Common';
import { CommonSummaryItem } from '../common/types';
import { ErrorMsgFormat } from '../error-handler-context/CommonTcoErrorHandler';
import { generateRdsErrorMsgs } from '../error-handler-context/helper';
import GenerateErrorMessageBox from '../error-handler-context/TcoErrorHandler';
import { buildRDSCostDetailsTabs, getCashFlowTable, getDatabaseLicensingTable, getPricingDetailsTable } from './RDSCostHelper';

type Props = FlashContextInterface;

const tcoService = new TcoService();

function RDSCost({ flash }: Props) {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const portfolioId = match?.params.portfolioId ?? null;
    const { assessment } = useContext(TcoContext);
   
    const [ loading, setLoading ] = useState(false);
    const [ rdsOutput, setRdsOutput ] = useState<OutputRDS | null>(null);
    const [ ec2Output, setEC2Output ] = useState<OutputEc2 | null>(null);
    const [ currency, setCurrency ] = useState<string>('');
    const [ activeTab, setActiveTab ] = useState<string>('cashFlow');
    const [ errorMessages, setErrorMessages ] = useState<ErrorMsgFormat[]>([]);
    const { panelHandler } = useContext(HelpPanelContext);
    
    const history = useHistory();

    const isMounted = useIsMounted();

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.RDS_COST);
    }, [ panelHandler ]);

    const loadRDSCostData = async () => {
        if (!isMounted()) {
            return;
        }

        setLoading(true);
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const awsRDSAssessment = await tcoService.getAwsRDS(tcoAssessmentId);
                const awsEC2Assessment = await tcoService.getAwsEC2(tcoAssessmentId);
                if (isMounted()) {
                    setRdsOutput(awsRDSAssessment.output.aws.rds);
                    setEC2Output(awsEC2Assessment.output.aws.ec2);
                    setCurrency(awsRDSAssessment.output.aws.currency);
                }
            }
        } catch(err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_RDS_ASSESSMENT);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (assessment) {
            loadRDSCostData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    useEffect(() => {
        if (!rdsOutput || !portfolioId || !ec2Output) {
            return;
        }
        setErrorMessages(generateRdsErrorMsgs(rdsOutput, ec2Output, portfolioId, tcoAssessmentId));
    }, [ rdsOutput, portfolioId, tcoAssessmentId, ec2Output ]);

    const buildSummaryItems = (): CommonSummaryItem[] => {
        return [
            {
                type: constants.AMAZON_DB,
                description: constants.AWS_DB_COST,
                cost: rdsOutput?.summary.totalRDSCost ?? 0,
            },
            {
                type: constants.DB_LICENSES,
                description: constants.DB_LICENSE_COST,
                cost: rdsOutput?.summary.totalLicenseCost ?? 0,
            },
            {
                type: constants.AMAZON_DB_RESIDUAL_VALUE,
                description: constants.VALUE_AFTER_TCO_DURATION,
                cost: rdsOutput?.summary.totalRDSResidual ?? 0,
            }
        ];
    };

    const buildTabs = ():TabsProps.Tab[] => {
        return (rdsOutput && assessment) ? buildRDSCostDetailsTabs(
            getCashFlowTable(currency, rdsOutput?.databases, assessment?.configuration.tcoAssumptions.durationYears),
            getPricingDetailsTable(currency, rdsOutput?.databases),
            getDatabaseLicensingTable(currency, rdsOutput?.licenseCost))
            : [];
    };

    const buildServerPageContent = (): JSX.Element => {
        if (loading || !rdsOutput || !assessment) {
            return (
                <Spinner size='big' />
            );
        }

        return (
            <SpaceBetween size='xl'>
                <PreviewBanner customText={constants.RDS_PREVIEW_TEXT} />
                <Header variant='h1'>{constants.AWS_DB_COST}</Header>
                <GenerateErrorMessageBox
                    errorMessages={errorMessages}
                    history={history}
                />
                <Table
                    columnDefinitions={summaryColumnDefs(currency)}
                    items={buildSummaryItems()}
                    sortingDisabled
                />
                {commonTotalCostSummary(currency, rdsOutput.summary.totalCost, rdsOutput.summary.manualAdjustment.value, rdsOutput.summary.totalAdjustedCost)}
                <Tabs tabs={buildTabs()} activeTabId={activeTab} onChange={({ detail }) => setActiveTab(detail.activeTabId)}/>
            </SpaceBetween>
        );
    };

    return (
        <Container data-testid='container'>
            {buildServerPageContent()}
        </Container>
    );
}

export default withFlashContext(RDSCost);
